import { useState, useEffect } from "react";
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, where } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import WorkflowSection from "../workflow/WorkflowSection";
import { BRAND, THEMES, STAFF_COLORS, STATUS_FLOW, STATUS_META } from "../../config/constants";
import { db } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { qbCall } from "../../utils/api";
import { btnP } from "../../utils/styles";

const storage = getStorage();

export default function PipelinePage({clients,leads,theme,mobile,staffSigs={},sigSaving={},saveSig,clearSig,onNavigateToClient,onNavigateToBiz,currentUser=""}){
  const t=THEMES[theme];
  const _isMobile=useMobile();
  const isMobile=mobile||_isMobile;
  const [unmatchedReturns,setUnmatchedReturns]=useState([]);
  const [assigningId,setAssigningId]=useState(null);
  const [assignTarget,setAssignTarget]=useState({});
  const [search,setSearch]=useState("");
  const [allBizPipeline,setAllBizPipeline]=useState([]);

  // Auto-refresh QB invoices for clients with pending invoices when pipeline opens
  useEffect(()=>{
    const pending=clients.filter(c=>c.qbCustomerId && c.status==="Invoice Pending");
    if(!pending.length) return;
    let cancelled=false;
    (async()=>{
      for(const c of pending){
        if(cancelled) break;
        try{ await qbCall("qbManualSyncInvoices",{crmClientId:c.id}); }catch(e){}
      }
    })();
    return ()=>{ cancelled=true; };
  },[clients.length]);

  useEffect(()=>{
    const unsub=onSnapshot(
      query(collection(db,"unmatched_returns"),where("resolved","==",false)),
      snap=>setUnmatchedReturns(snap.docs.map(d=>({id:d.id,...d.data()})))
    );
    return unsub;
  },[]);

  // Load all businesses for pipeline
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"businesses"),snap=>{
      setAllBizPipeline(snap.docs.map(d=>({id:d.id,...d.data()})));
    });
    return unsub;
  },[]);

  const assignReturn=async(unmatchedId,clientId,year,filename,filePath)=>{
    if(!clientId){alert("Please select a client first.");return;}
    setAssigningId(unmatchedId);
    try{
      const fileRef=ref(storage,filePath);
      const url=await getDownloadURL(fileRef);
      await updateDoc(doc(db,"clients",clientId),{
        [`taxReturnUrl_${year}`]:url,[`taxReturnFile_${year}`]:filename,
        [`taxReturnUploadedAt_${year}`]:serverTimestamp(),updatedAt:serverTimestamp(),
      });
      await updateDoc(doc(db,"unmatched_returns",unmatchedId),{resolved:true,resolvedAt:serverTimestamp(),resolvedClientId:clientId});
    }catch(e){alert("Assign failed: "+e.message);}
    setAssigningId(null);
  };

  const filterClient = c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase());
  const clientMap = Object.fromEntries(clients.map(c=>[c.id,c]));
  const filterBiz = b => !search || b.legalName?.toLowerCase().includes(search.toLowerCase()) || (clientMap[b.ownerId]?.name||"").toLowerCase().includes(search.toLowerCase());

  return(
    <div>
      {/* Workflow section */}
      <WorkflowSection currentUser={currentUser} theme={theme} clients={clients} onNavigateToClient={onNavigateToClient}/>

      {/* Slim stat bar */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        {[
          {label:"Clients", val:clients.length, color:BRAND.blue},
          {label:"Leads",   val:leads.length,   color:"#f59e0b"},
          {label:"Awaiting Sign", val:clients.filter(c=>c.status==="Engagement Sent").length, color:BRAND.green},
          {label:"In Progress",   val:clients.filter(c=>c.status==="Tax Return in Progress").length, color:"#f59e0b"},
          {label:"Extension Filed", val:clients.filter(c=>c.status==="Extension Filed").length, color:"#e85d04"},
          {label:"Completed",     val:clients.filter(c=>c.status==="Completed").length, color:BRAND.green},
        ].map(s=>(
          <div key={s.label} style={{display:"flex",alignItems:"center",gap:8,background:t.surface,border:`1px solid ${s.color}30`,borderRadius:10,padding:"7px 14px",borderLeft:`3px solid ${s.color}`}}>
            <span style={{fontSize:18,fontWeight:800,color:s.color,letterSpacing:"-0.5px"}}>{s.val}</span>
            <span style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{s.label}</span>
          </div>
        ))}
        {/* Search */}
        <div style={{marginLeft:"auto",position:"relative",minWidth:180}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients..."
            style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:10,padding:"7px 12px 7px 30px",color:t.text,fontSize:12.5,outline:"none",fontFamily:"'Outfit',sans-serif",width:"100%",boxSizing:"border-box"}}/>
          <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:t.textMuted}}>🔍</span>
        </div>
      </div>

      {/* Kanban board */}
      <div style={{display:"flex",gap:10,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:8,alignItems:"flex-start"}}>
        {STATUS_FLOW.filter(s=>s!=="Not Started").map(s=>{
          const m=STATUS_META[s];
          // Merge client rows + business rows for this status column
          const clientRows = clients.filter(c=>c.status===s&&filterClient(c)).map(c=>({...c,_kind:"client"}));
          const bizRows = allBizPipeline.filter(b=>(b.status||"Not Started")===s&&filterBiz(b)).map(b=>({...b,_kind:"business"}));
          const cols = [...clientRows,...bizRows];
          return(
            <div key={s} style={{flex:"0 0 260px",minWidth:260,background:theme==="dark"?"#111820":"#f4f6f8",borderRadius:14,padding:"0 0 8px 0",border:`1px solid ${t.border}`}}>
              {/* Column header */}
              <div style={{padding:"12px 14px 10px",borderBottom:`2px solid ${m.text}40`,borderRadius:"14px 14px 0 0",background:m.bg(theme)}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,fontWeight:700,color:m.text,flex:1}}>{s}</span>
                  <span style={{fontSize:11,fontWeight:800,color:"#fff",background:m.text,padding:"2px 8px",borderRadius:10,minWidth:20,textAlign:"center"}}>{cols.length}</span>
                </div>
              </div>
              {/* Cards */}
              <div style={{padding:"8px 8px 0",display:"flex",flexDirection:"column",gap:7,maxHeight:"calc(100vh - 240px)",overflowY:"auto"}}>
                {cols.length===0?(
                  <div style={{padding:"16px 10px",textAlign:"center",color:t.textMuted,fontSize:11.5,fontStyle:"italic"}}>Empty</div>
                ):cols.map(c=>{
                  const isBiz = c._kind==="business";
                  const owner = isBiz ? clientMap[c.ownerId] : null;
                  const borderAccent = isBiz ? BRAND.green : m.text;
                  return(
                    <div key={(isBiz?"biz-":"cli-")+c.id}
                      onClick={()=>isBiz ? onNavigateToBiz({biz:c,ownerClient:owner}) : onNavigateToClient(c)}
                      style={{background:t.surface,borderRadius:10,padding:"10px 12px",
                        border:`1px solid ${isBiz?BRAND.green+"40":t.border}`,
                        borderLeft:`3px solid ${borderAccent}`,
                        cursor:"pointer",transition:"all 0.12s",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=borderAccent+"80";e.currentTarget.style.transform="translateY(-1px)";}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor=isBiz?BRAND.green+"40":t.border;e.currentTarget.style.transform="translateY(0)";}}
                    >
                      {/* Name row */}
                      <div style={{fontSize:12.5,fontWeight:700,color:t.text,marginBottom:4,lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {isBiz ? c.legalName : c.name}
                        {!isBiz&&c.spouseName&&<span style={{fontSize:11,fontWeight:400,color:t.textMuted}}> & {c.spouseName}</span>}
                      </div>
                      {/* Type badge + owner (for biz) */}
                      {isBiz&&owner&&(
                        <div style={{fontSize:10.5,color:t.textMuted,marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                          👤 {owner.name}
                        </div>
                      )}
                      {/* Bottom row */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          {isBiz
                            ? <span style={{fontSize:9.5,fontWeight:700,color:BRAND.green,background:BRAND.green+"18",padding:"1px 6px",borderRadius:6}}>🏢 {c.entityType||"Biz"}</span>
                            : <span style={{fontSize:9.5,fontWeight:700,color:BRAND.blue,background:BRAND.blue+"18",padding:"1px 6px",borderRadius:6}}>👤 Individual</span>
                          }
                        </div>
                        <span style={{fontSize:10,color:STAFF_COLORS[c.assignedTo]||t.textMuted,fontWeight:700,background:(STAFF_COLORS[c.assignedTo]||"#888")+"18",padding:"1px 6px",borderRadius:6}}>{c.assignedTo}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Integrations — compact */}
      <div style={{marginTop:14,background:t.surface,border:`1px solid ${t.border}`,borderRadius:12,padding:"10px 16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <span style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700,flexShrink:0}}>Integrations</span>
          {[{name:"Firebase",color:BRAND.green},{name:"Firestore",color:BRAND.green},{name:"Storage",color:BRAND.green},{name:"DocuSeal",color:BRAND.green},{name:"QuickBooks",color:BRAND.blue},{name:"Website",color:BRAND.green}].map(i=>(
            <div key={i.name} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:5,height:5,borderRadius:"50%",background:i.color,flexShrink:0}}/>
              <span style={{fontSize:11,color:t.textSub}}>{i.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Unmatched Tax Returns */}
      {unmatchedReturns.length>0&&(
        <div style={{background:t.surface,border:"1px solid #f59e0b40",borderRadius:16,padding:20,marginTop:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#f59e0b"}}/>
            <div style={{fontWeight:800,fontSize:14,color:t.text}}>Unmatched Tax Returns</div>
            <span style={{fontSize:11,fontWeight:800,color:"#f59e0b",background:"#f59e0b20",padding:"2px 9px",borderRadius:10,border:"1px solid #f59e0b35"}}>{unmatchedReturns.length}</span>
          </div>
          {unmatchedReturns.map(u=>(
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:t.bg,borderRadius:10,border:`1px solid ${t.border}`,marginBottom:8,flexWrap:"wrap"}}>
              <Icon name="file" size={16} color="#f59e0b"/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:700,color:t.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.filename}</div>
                <div style={{fontSize:11,color:t.textMuted}}>Year: {u.year} · {u.reason}</div>
              </div>
              <select style={{background:t.inputBg,border:`1px solid ${t.border}`,borderRadius:8,padding:"7px 10px",color:t.text,fontSize:12,outline:"none",fontFamily:"'Outfit',sans-serif",width:200}}
                value={assignTarget[u.id]||""} onChange={e=>setAssignTarget(prev=>({...prev,[u.id]:e.target.value}))}>
                <option value="">Select client...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}{c.spouseName?` & ${c.spouseName}`:""}</option>)}
              </select>
              <button onClick={()=>assignReturn(u.id,assignTarget[u.id],u.year,u.filename,u.filePath)}
                disabled={assigningId===u.id||!assignTarget[u.id]}
                style={{...btnP,opacity:!assignTarget[u.id]?0.5:1}}>
                {assigningId===u.id?<><MiniSpinner size={13} color="#fff"/>Saving...</>:"Assign"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Login
